import { createContext, useContext, useEffect } from 'react'

/**
 * Lightweight page-title bus for the dashboard top bar.
 * Pages call setPageTitle() inside a useEffect; the header reads it.
 * A default derived from the route is used when a page doesn't set one.
 */
interface PageTitleContextValue {
  title: string | null
  setTitle: (title: string | null) => void
}

export const PageTitleContext = createContext<PageTitleContextValue | null>(null)

export function usePageTitleContext(): PageTitleContextValue {
  const ctx = useContext(PageTitleContext)
  if (!ctx) {
    // Non-fatal — pages outside the provider just get a no-op setter.
    return { title: null, setTitle: () => {} }
  }
  return ctx
}

/** Convenience hook: set the page title for the current mount. */
export function usePageTitle(title: string | null): void {
  const { setTitle } = usePageTitleContext()
  useEffect(() => {
    setTitle(title)
    return () => setTitle(null)
  }, [title, setTitle])
}
